import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { TEXTS } from '../constants';
import { GameBoat } from './svg/BoatSVG';

const UnlockGame = ({ onComplete }) => {
  const [gameState, setGameState] = useState('playing'); // playing, success
  const [targetPosition, setTargetPosition] = useState({ x: 50, y: 50 });
  const [tapCount, setTapCount] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isDisappearing, setIsDisappearing] = useState(false);
  const [difficulty, setDifficulty] = useState(1);
  const containerRef = useRef(null);
  const requiredTaps = 8; // Increased from 5 to 8

  // Move target to random position with more distance
  const moveTarget = useCallback((fast = false) => {
    const currentX = targetPosition.x;
    const currentY = targetPosition.y;
    
    // Move farther away from current position
    let newX, newY;
    do {
      newX = 10 + Math.random() * 80;
    } while (Math.abs(newX - currentX) < 25);
    
    do {
      newY = 20 + Math.random() * 55;
    } while (Math.abs(newY - currentY) < 20);
    
    setTargetPosition({ x: newX, y: newY });
  }, [targetPosition.x, targetPosition.y]);

  // Teleport - disappear and reappear elsewhere (faster)
  const teleportTarget = useCallback(() => {
    setIsDisappearing(true);
    setTimeout(() => {
      const newX = 5 + Math.random() * 90;
      const newY = 15 + Math.random() * 60;
      setTargetPosition({ x: newX, y: newY });
      setIsDisappearing(false);
    }, 200); // Faster teleport
  }, []);

  // Initial position
  useEffect(() => {
    setTargetPosition({ x: 50, y: 45 });
  }, []);

  // Continuous movement - boat drifts around more aggressively
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const baseInterval = Math.max(300, 700 - difficulty * 80);
    
    const interval = setInterval(() => {
      if (!isDisappearing) {
        // More aggressive drift based on difficulty
        const drift = 8 + difficulty * 4;
        setTargetPosition(prev => ({
          x: Math.max(5, Math.min(95, prev.x + (Math.random() - 0.5) * drift * 2)),
          y: Math.max(15, Math.min(75, prev.y + (Math.random() - 0.5) * drift))
        }));
      }
    }, baseInterval);
    
    return () => clearInterval(interval);
  }, [gameState, isDisappearing, difficulty]);

  // Show hint after a few seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (gameState === 'playing' && tapCount < 2) {
        setShowHint(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [gameState, tapCount]);

  // Handle tap on target
  const handleTap = () => {
    if (isDisappearing) return;
    
    const newCount = tapCount + 1;
    setTapCount(newCount);
    setShowHint(false);

    if (newCount >= requiredTaps) {
      // Success!
      setGameState('success');
      triggerConfetti();
      setTimeout(onComplete, 2000);
    } else {
      // Increase difficulty every 2 taps
      const newDifficulty = Math.floor(newCount / 2) + 1;
      setDifficulty(newDifficulty);
      
      if (newCount >= 5) {
        // After 5 taps, start teleporting
        teleportTarget();
      } else if (newCount >= 3) {
        // After 3 taps, faster movement
        setTimeout(() => {
          moveTarget(true);
        }, 50);
      } else {
        // Early taps - regular movement
        setTimeout(() => {
          moveTarget();
        }, 100);
      }
    }
  };

  // Confetti explosion
  const triggerConfetti = () => {
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ['#FACC15', '#0EA5E9', '#EC4899', '#22C55E']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ['#FACC15', '#0EA5E9', '#EC4899', '#22C55E']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };

  const getDifficultyMessage = () => {
    if (tapCount >= 6) return '🔥 Fast gschafft!';
    if (tapCount >= 5) return '⚡ Es teleportiert!';
    if (tapCount >= 3) return '⚡ Jetzt wird\'s schneller!';
    return null;
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden no-select"
      data-testid="unlock-game-container"
    >
      <AnimatePresence mode="wait">
        {gameState === 'playing' && (
          <motion.div
            key="playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full h-full relative"
          >
            {/* Title */}
            <div className="absolute top-8 left-0 right-0 text-center z-10 px-4">
              <motion.h2 
                className="text-2xl font-black text-slate-900 mb-2"
                style={{ fontFamily: 'Nunito, sans-serif' }}
                data-testid="unlock-title"
              >
                {TEXTS.unlockTitle}
              </motion.h2>
              <p className="text-slate-600">{TEXTS.unlockSubtitle}</p>
              
              {/* Progress */}
              <div className="flex justify-center gap-1.5 mt-4 flex-wrap max-w-xs mx-auto">
                {Array.from({ length: requiredTaps }).map((_, i) => (
                  <motion.div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < tapCount ? 'bg-yellow-400' : 'bg-white/50'
                    }`}
                    animate={i < tapCount ? { scale: [1, 1.3, 1] } : {}}
                    data-testid={`tap-progress-${i}`}
                  />
                ))}
              </div>
              
              {/* Difficulty indicator */}
              {getDifficultyMessage() && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-orange-500 mt-3 font-semibold"
                >
                  {getDifficultyMessage()}
                </motion.p>
              )}
            </div>

            {/* Hint */}
            <AnimatePresence>
              {showHint && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-44 left-0 right-0 text-center z-10"
                >
                  <span className="glass px-4 py-2 rounded-full text-sm text-slate-600">
                    {TEXTS.unlockHint} 👆
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Moving target (boat) */}
            <motion.button
              onClick={handleTap}
              animate={{
                left: `${targetPosition.x}%`,
                top: `${targetPosition.y}%`,
                opacity: isDisappearing ? 0 : 1,
                scale: isDisappearing ? 0.2 : 1,
              }}
              transition={{
                type: 'spring',
                stiffness: 200 + difficulty * 50,
                damping: 20 - difficulty * 2,
                opacity: { duration: 0.1 },
                scale: { duration: 0.1 }
              }}
              whileTap={{ scale: 0.85 }}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer focus:outline-none"
              style={{ touchAction: 'manipulation' }}
              data-testid="unlock-target"
              disabled={isDisappearing}
            >
              <motion.div
                animate={{ 
                  rotate: [-8, 8, -8],
                  y: [0, -6, 0]
                }}
                transition={{ 
                  duration: Math.max(0.5, 1.2 - difficulty * 0.15), 
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className="relative"
              >
                <GameBoat />
                {/* Glow effect - more intense at higher difficulty */}
                <div 
                  className="absolute inset-0 -z-10 blur-xl rounded-full scale-150"
                  style={{ 
                    background: `rgba(250, 204, 21, ${0.2 + difficulty * 0.1})` 
                  }}
                />
              </motion.div>
            </motion.button>

            {/* Tap counter */}
            <div className="absolute bottom-28 left-0 right-0 text-center">
              <span className="text-4xl font-black text-slate-300">
                {tapCount}/{requiredTaps}
              </span>
            </div>

            {/* Water effect at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-sky-300/50 to-transparent pointer-events-none" />
          </motion.div>
        )}

        {gameState === 'success' && (
          <motion.div
            key="success"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="text-center px-6"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: 3 }}
              className="text-7xl mb-6"
            >
              ✅
            </motion.div>
            <h2 
              className="text-3xl font-black text-slate-900 mb-2"
              style={{ fontFamily: 'Nunito, sans-serif' }}
              data-testid="unlock-success-text"
            >
              1/2 erledigt!
            </h2>
            <p className="text-lg text-slate-600 mb-4">Jetzt no eins...</p>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 1.5 }}
              className="h-1 bg-gradient-to-r from-yellow-400 via-sky-400 to-pink-400 rounded-full max-w-xs mx-auto"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UnlockGame;
